import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// ─── Types ───────────────────────────────────────────────────────────────────
interface LoadingPageProps {
  /** Called when the loading animation completes (optional) */
  onFinished?: () => void;
  /** Backward-compatible completion callback */
  onLoadingComplete?: () => void;
  /** Duration in ms before triggering onFinished. Default: 3500 */
  duration?: number;
  /** Path / URL to the chick GIF */
  gifSrc?: string;
  /** Redirect to home route after loading */
  redirectToHome?: boolean;
}

// ─── Dot loader ───────────────────────────────────────────────────────────────
const Dot = ({ delay }: { delay: string }) => (
  <span
    className="block w-3 h-3 rounded-full bg-orange-500"
    style={{ animation: `pulse-dot 1.2s ${delay} ease-in-out infinite` }}
  />
);

// ─── Main component ───────────────────────────────────────────────────────────
export default function LoadingPage({
  onFinished,
  onLoadingComplete,
  duration = 3500,
  redirectToHome = true,
}: LoadingPageProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    const complete = onFinished ?? onLoadingComplete;
    const fadeTimer = setTimeout(() => setFadeOut(true), duration - 600);
    const hideTimer = setTimeout(() => {
      if (redirectToHome && typeof window !== "undefined" && window.location.pathname !== "/") {
        window.history.replaceState(null, "", "/");
      }
      setVisible(false);
      complete?.();
    }, duration);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, [duration, onFinished, onLoadingComplete, redirectToHome]);

  if (!visible) return null;

  return (
    <>
      {/* Keyframe styles injected once */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@700;800&family=Nunito:wght@600;700&display=swap');

        @keyframes float-chick {
          0%, 100% { transform: translateY(0px) rotate(-1deg); }
          50%       { transform: translateY(-18px) rotate(1deg); }
        }
        @keyframes spin-ring {
          to { transform: rotate(360deg); }
        }
        @keyframes shimmer-bar {
          0%   { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes pulse-dot {
          0%, 80%, 100% { transform: scale(1);   opacity: 0.35; }
          40%            { transform: scale(1.6); opacity: 1;    }
        }
        @keyframes fade-up {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        @keyframes blob-drift {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50%       { transform: translate(20px, -20px) scale(1.08); }
        }

        .font-baloo   { font-family: 'Baloo 2', cursive; }
        .font-nunito  { font-family: 'Nunito', sans-serif; }

        .anim-float   { animation: float-chick 3.2s ease-in-out infinite; }
        .anim-spin    { animation: spin-ring 4s linear infinite; }
        .anim-shimmer { 
          background: linear-gradient(90deg, #ff6b00, #ffb347, #ffd580, #ffb347, #ff6b00);
          background-size: 200% 100%;
          animation: shimmer-bar 2s linear infinite;
        }
        .anim-blob    { animation: blob-drift 8s ease-in-out infinite; }

        .fade-up-0 { animation: fade-up 0.7s 0.0s ease both; }
        .fade-up-1 { animation: fade-up 0.7s 0.3s ease both; }
        .fade-up-2 { animation: fade-up 0.7s 0.6s ease both; }
        .fade-up-3 { animation: fade-up 0.7s 0.9s ease both; }
      `}</style>

      {/* Overlay wrapper */}
      <div
        className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white overflow-hidden"
        style={{
          transition: "opacity 0.6s ease",
          opacity: fadeOut ? 0 : 1,
          pointerEvents: fadeOut ? "none" : "auto",
        }}
      >
        {/* ── Background blobs ── */}
        <div
          className="anim-blob absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(255,107,0,0.12) 0%, transparent 70%)",
          }}
        />
        <div
          className="anim-blob absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full"
          style={{
            animationDelay: "4s",
            background:
              "radial-gradient(circle, rgba(255,179,71,0.10) 0%, transparent 70%)",
          }}
        />

        {/* ── Content card ── */}
        <div className="relative flex flex-col items-center gap-0 select-none">

          {/* GIF with spinning glow ring — fade-up-0 */}
          <div className="fade-up-0 relative flex items-center justify-center w-60 h-60">
            {/* Spinning gradient ring (blurred) */}
            <div
              className="anim-spin absolute inset-0 rounded-full"
              style={{
                background:
                  "conic-gradient(#ff6b00, #ffb347, #ffd580, #ffb347, #ff6b00)",
                filter: "blur(10px)",
                opacity: 0.3,
              }}
            />
            {/* Inner white cut-out ring */}
            <div className="absolute inset-2 rounded-full bg-white" />
            {/* Floating chick GIF */}
            <img
              src="/congratulation kids.gif"
              alt={t("loader.chickAlt", { defaultValue: "Chick waving hello" })}
              className="anim-float relative z-10 w-52 h-52 object-contain"
              style={{
                filter: "drop-shadow(0 12px 28px rgba(255,107,0,0.25))",
              }}
            />
          </div>

          {/* "You're in!" badge — fade-up-1 */}
          <div className="fade-up-1 mt-5">
            <span className="font-nunito inline-block px-4 py-1 rounded-full bg-orange-50 border border-orange-200 text-orange-400 text-xs font-semibold tracking-widest uppercase">
              {t("loader.badge", { defaultValue: "Haapa Store" })}
            </span>
          </div>

          {/* Main heading — fade-up-2 */}
          <div className="fade-up-2 mt-3 text-center leading-none">
            <h1 className="font-baloo text-4xl sm:text-5xl font-extrabold tracking-tight whitespace-nowrap">
              <span className="text-orange-500">{t("loader.welcomePrefix", { defaultValue: "Welcome to " })}</span>
              <span className="text-orange-400">{t("loader.brandPrimary", { defaultValue: "Haapa" })}</span>
              <span className="text-orange-600">{t("loader.brandSuffix", { defaultValue: " Store" })}</span>
            </h1>
          </div>

          {/* Sub-label — fade-up-2 */}
          <p className="fade-up-2 font-nunito mt-2 text-orange-300 text-sm tracking-wide">
            {t("loader.subtitle", { defaultValue: "Getting everything ready for you..." })}
          </p>

          {/* Dots — fade-up-3 */}
          <div className="fade-up-3 flex gap-3 mt-8">
            <Dot delay="0s" />
            <Dot delay="0.2s" />
            <Dot delay="0.4s" />
          </div>
        </div>

        {/* ── Bottom shimmer strip ── */}
        <div className="anim-shimmer absolute bottom-0 left-0 right-0 h-1.5" />
      </div>
    </>
  );
}
